import { useState, useEffect, useRef } from 'react';
import { Download, Smartphone, CheckCircle2, PlusSquare, ArrowUp, AlertTriangle, User, GraduationCap, School, Users, ShieldCheck, Trash2, Info, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';
import { clearAllPasskeysOnThisDevice } from '../../auth/passkeyDevice';
import { clearClientSession } from '../../auth/sessionCleanup';
import { revokeCurrentDevicePasskeyWithExplicitConsent } from '../../security/passkey/passkeyApi';
import { registerApprovalDevice } from '../../security/deviceApprovalApi';

const getProfileIcon = (perfil: string) => {
  switch (perfil) {
    case 'ADMIN':
      return <ShieldCheck size={20} style={{ color: '#c084fc' }} />;
    case 'PROFESSOR':
      return <GraduationCap size={20} style={{ color: '#c084fc' }} />;
    case 'ALUNO':
      return <GraduationCap size={20} style={{ color: '#c084fc' }} />;
    case 'ESCOLA':
      return <School size={20} style={{ color: '#c084fc' }} />;
    case 'RESPONSAVEL':
      return <Users size={20} style={{ color: '#c084fc' }} />;
    default:
      return <User size={20} style={{ color: '#c084fc' }} />;
  }
};

const getProfileLabel = (user: any) => {
  if (user.perfil === 'ESCOLA' && user.cargo) {
    const cargoLabels: Record<string, string> = {
      DIRETOR: 'Diretor',
      COORDENACAO: 'Coordenador',
      SECRETARIA: 'Secretaria',
    };
    return cargoLabels[user.cargo] || user.cargo;
  }
  const profileLabels: Record<string, string> = {
    ADMIN: 'ADMIN',
    PROFESSOR: 'Professor',
    ALUNO: 'Aluno',
    RESPONSAVEL: 'Responsavel',
    MUNICIPAL: 'Prefeitura',
    ESTADUAL: 'Estadual',
    REDE: 'Rede',
    PUBLICO: 'Visitante',
  };
};

const getUserInitials = (name: string) => {
  const parts = (name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return 'AC';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

interface Props {
  onSuccess: () => void;
  onSkip: () => void;
  showAccountCard?: boolean;
  autoPrompt?: boolean;
}

/**
 * PwaInstaller — Etapa do bonding que oferece instalacao do PWA.
 *
 * - O evento beforeinstallprompt ja foi capturado globalmente em main.tsx
 *   e armazenado em window.__pwaInstallPrompt
 * - Chromium: usa o prompt capturado para acionar o dialogo nativo
 * - iOS: mostra instrucoes visuais manuais (Compartilhar > Adicionar)
 * - Se ja instalado (standalone): auto-skip
 * - Tratamento de erros em todas as etapas
 */
export function PwaInstaller({ onSuccess, onSkip, showAccountCard = true, autoPrompt = false }: Props) {
  const { user, logout } = useAuthStore();
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(() => {
    const isStandaloneInit =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true ||
      window.location.protocol === 'capacitor:' ||
      !!(window as any).Capacitor;
    return isStandaloneInit;
  });
  const [error, setError] = useState('');
  const [isDetected, setIsDetected] = useState(false);
  const [desconectando, setDesconectando] = useState(false);
  const [successHelp, setSuccessHelp] = useState<'none' | 'abrir' | 'desinstalar'>('none');
  const [checking, setChecking] = useState(true);
  const promptRef = useRef<any>(null);

  const handleDesconectar = async () => {
    const confirmou = window.confirm(
      'Atenção: Ao desconectar este dispositivo, você revogará o acesso biométrico (Passkey) e desativará o vínculo deste aparelho com sua conta. Você poderá ficar sem acesso ao sistema e ter que usar outros recursos para acessar.\n\nTem certeza de que deseja continuar?'
    );

    if (!confirmou) return;

    setDesconectando(true);
    try {
      await revokeCurrentDevicePasskeyWithExplicitConsent({
        explicitConsent: true,
        reason: 'pwa-installer-user-confirmed-disconnect',
      });
    } catch (err) {
      console.error('[PWA] Erro ao revogar biometria:', err);
    } finally {
      // 2. Limpar storages locais adicionais para garantir que não haja rastros de biometria mestre local
      try {
        localStorage.removeItem('ac_pwa_installed');
        localStorage.removeItem('ac_remember_email');
        
        clearAllPasskeysOnThisDevice();
      } catch (e) {
        console.warn('[PWA] Erro ao limpar storages locais:', e);
      }

      // 3. Executar o logout global no authStore para limpar estado e redirecionar para login
      try {
        await logout();
      } catch (logoutErr) {
        console.error('[PWA] Erro no logout global:', logoutErr);
        await clearClientSession();
        window.location.href = '/login';
      }
      setDesconectando(false);
    }
  };

  // Detectar iOS (todos os browsers no iOS usam WebKit)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

  // Detectar Android e Windows
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isWindows = /Windows/i.test(navigator.userAgent);

  // Verificar se ja esta instalado (modo standalone = ja e PWA)
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;

  useEffect(() => {
    if (isStandalone) {
      setInstalled(true);
      setTimeout(() => {
        if (!successCalledRef.current) {
          successCalledRef.current = true;
          onSuccess();
        }
      }, 1000);
      return;
    }

    // Check de desvio do PWA (instalado anteriormente mas aberto no browser convencional)
    const checkPwaInstalledDeviation = async () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
      if (isMobileDevice && !isStandalone) {
        const localFlag = localStorage.getItem('ac_pwa_installed') === 'true';
        const apiFlag = user?.preferencias?.pwaInstalado === true;
        
        if (localFlag || apiFlag) {
          console.warn('[PWA Telemetry] Alerta: O aplicativo PWA foi aberto fora do modo Standalone.');
          try {
            const tk = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token');
            if (tk) {
              await registerApprovalDevice(
                {
                  fingerprint: getDeviceFingerprint(),
                  nome: 'Mobile fora do PWA — ' + navigator.userAgent.substring(0, 20),
                  tipoLocal: 'DESVIO_PWA',
                  somenteMeu: true,
                },
                { bearerToken: tk },
              );
            }
          } catch (e) {
            // ignore
          }
        }
      }
    };
    checkPwaInstalledDeviation();

    // Pegar prompt ja capturado globalmente no main.tsx
    if ((window as any).__pwaInstallPrompt) {
      promptRef.current = (window as any).__pwaInstallPrompt;
    }

    // Tambem escutar caso o evento ainda nao tenha disparado
    const handler = (e: Event) => {
      e.preventDefault();
      promptRef.current = e;
      (window as any).__pwaInstallPrompt = e;
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Detectar instalacao concluida
    const appInstalledHandler = async () => {
      setInstalled(true);
      try {
        localStorage.setItem('ac_pwa_installed', 'true');
        const API = import.meta.env.VITE_API_URL || '/api/v1';
        const tk = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token');
        if (tk) {
          await fetch(API + '/auth/me/preferencias', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + tk
            },
            body: JSON.stringify({ pwaInstalado: true })
          });
        }
      } catch (e) {
        console.warn('[PWA Telemetry] Erro ao salvar preferencia de PWA:', e);
      }
    };
    window.addEventListener('appinstalled', appInstalledHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', appInstalledHandler);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof window !== 'undefined' && window.self === window.top && 'getInstalledRelatedApps' in navigator) {
      (navigator as any).getInstalledRelatedApps()
        .then((apps: any[]) => {
          if (apps && apps.length > 0) {
            setIsDetected(true);
          }
        })
        .catch((e: any) => console.log('[PWA] Erro ao verificar apps relacionados:', e));
    }
  }, []);

  const handleInstall = async () => {
    setError('');

    // Tentar pegar o prompt global novamente (pode ter chegado depois do mount)
    if (!promptRef.current && (window as any).__pwaInstallPrompt) {
      promptRef.current = (window as any).__pwaInstallPrompt;
    }

    if (!promptRef.current) {
      setError('O navegador nao ofereceu a opcao de instalacao. Verifique se voce esta usando Chrome ou Edge e tente recarregar a pagina.');
      return;
    }

    setInstalling(true);
    try {
      const p = promptRef.current.prompt();
      if (p && typeof p.catch === 'function') {
        p.catch((err: any) => {
          console.warn('[PWA] prompt() catch:', err);
        });
      }
      
      // Executa userChoice sem dar await para evitar travar se for bloqueado
      promptRef.current.userChoice.then((result: any) => {
        if (result.outcome === 'accepted') {
          console.log('[PWA] Usuario aceitou instalacao');
        } else {
          console.log('[PWA] Usuario recusou instalacao');
          onSkip();
        }
        setInstalling(false);
      }).catch(() => {
        setInstalling(false);
      });

      // Timeout de segurança de 1.5s para destravar o botão caso o navegador bloqueie silenciosamente
      setTimeout(() => {
        setInstalling(false);
      }, 1500);

    } catch (e: any) {
      console.error('[PWA] Erro na instalacao:', e);
      setInstalling(false);
      if (e?.message?.toLowerCase().includes('gesture') || e?.name === 'NotAllowedError') {
        return;
      }
      setError(`Falha ao instalar: ${e?.message || 'Erro desconhecido'}. Tente novamente.`);
    }
  };

  const handleOpenApp = () => {
    localStorage.setItem('ac_skip_pwa_install', 'true');
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    if (isIOSDevice) {
      onSuccess();
      return;
    }
    const sessionId = localStorage.getItem('bonding_session_id') || sessionStorage.getItem('bonding_session_id') || '';
    const customScheme = `br.com.alunoconectado.app://security/bonding?s=${sessionId}`;
    
    try {
      window.location.href = customScheme;
    } catch {
      // Ignora falhas de esquema personalizado
    }

    setTimeout(() => {
      onSuccess();
    }, 1500);
  };

  // Countdown visual para a tela de sucesso
  const [successCountdown, setSuccessCountdown] = useState(15);
  const successCalledRef = useRef(false);

  useEffect(() => {
    if (!installed) return;
    const iv = setInterval(() => {
      setSuccessCountdown(prev => {
        if (prev <= 1) {
          clearInterval(iv);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [installed]);

  // Quando o contador chegar a 0, executa o redirecionamento automático
  useEffect(() => {
    if (installed && successCountdown === 0) {
      if (!successCalledRef.current) {
        successCalledRef.current = true;
        onSuccess();
      }
    }
  }, [installed, successCountdown, onSuccess]);

  // Timer de 250ms para evitar flashes de layouts durante a verificação assíncrona
  useEffect(() => {
    const t = setTimeout(() => {
      setChecking(false);
    }, 250);
    return () => clearTimeout(t);
  }, []);

  // Aciona a janela de instalacao nativa do PWA no primeiro gesto do usuario se autoPrompt estiver habilitado.
  // Como browsers modernos exigem um gesto do usuario, instalamos um escutador para o primeiro toque/clique do usuario na tela.
  useEffect(() => {
    if (autoPrompt && !installed && !isIOS && !checking) {
      const promptEvent = promptRef.current || (window as any).__pwaInstallPrompt;
      if (!promptEvent) return;

      const triggerOnGesture = () => {
        handleInstall();
        cleanup();
      };

      const cleanup = () => {
        window.removeEventListener('click', triggerOnGesture);
      };

      window.addEventListener('click', triggerOnGesture);

      return () => {
        cleanup();
      };
    }
  }, [autoPrompt, installed, isIOS, checking]);

  const renderUserInfo = () => {
    if (!user) return null;
    const isUserAdmin = user.perfil === 'ADMIN';
    const userName = user.nome || 'Conta conectada';
    const userEmail = user.email || 'Sessao autenticada';
    const userProfile = getProfileLabel(user);
    const userAvatarUrl = typeof user.fotoUrl === 'string' ? user.fotoUrl : '';
    const userInitials = getUserInitials(userName);

    return (
      <div style={{
        marginTop: '1.25rem',
        padding: '15px',
        borderRadius: 18,
        background: 'linear-gradient(145deg, rgba(38, 15, 87, 0.86), rgba(15, 9, 36, 0.70))',
        border: '1px solid rgba(216, 180, 254, 0.30)',
        boxShadow: '0 18px 44px rgba(24, 9, 54, 0.34), inset 0 1px 0 rgba(255,255,255,0.10)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        textAlign: 'left',
        boxSizing: 'border-box',
        width: '100%',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {userAvatarUrl ? (
            <img src={userAvatarUrl} alt="" style={{
              width: 48,
              height: 48,
              borderRadius: 17,
              objectFit: 'cover',
              flexShrink: 0,
              background: 'rgba(255,255,255,0.12)',
              boxShadow: '0 12px 26px rgba(91,33,182,0.42), inset 0 1px 0 rgba(255,255,255,0.22)',
            }} />
          ) : (
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 17,
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: 'linear-gradient(145deg, rgba(139,92,246,1), rgba(76,29,149,0.96))',
              boxShadow: '0 12px 26px rgba(91,33,182,0.42), inset 0 1px 0 rgba(255,255,255,0.22)',
              fontSize: '1rem',
              fontWeight: 900,
              letterSpacing: 0,
            }}>{userInitials}</div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
              <span style={{
                color: '#ffffff',
                fontSize: '0.98rem',
                fontWeight: 850,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}>{userName}</span>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 7px',
                borderRadius: 999,
                background: isUserAdmin ? 'rgba(239, 68, 68, 0.14)' : 'rgba(16, 185, 129, 0.14)',
                color: isUserAdmin ? '#fca5a5' : '#86efac',
                border: isUserAdmin ? '1px solid rgba(239, 68, 68, 0.24)' : '1px solid rgba(52, 211, 153, 0.24)',
                fontSize: '0.64rem',
                fontWeight: 850,
                textTransform: 'uppercase',
                flexShrink: 0,
              }}>
                <ShieldCheck size={13} strokeWidth={2.2} />
                {userProfile}
              </span>
            </div>
          </div>
        </div>
        <div style={{
          width: '100%',
          marginTop: 2,
          paddingTop: 10,
          borderTop: '1px solid rgba(216, 180, 254, 0.18)',
          color: '#ddd6fe',
          fontSize: '0.82rem',
          fontWeight: 600,
          lineHeight: 1.25,
          overflowWrap: 'anywhere',
        }}>{userEmail}</div>
        
        {/* Linha de baixo: botão Desconectar */}
        <button
          onClick={handleDesconectar}
          disabled={desconectando}
          style={{
            width: '100%',
            padding: '10px 14px',
            borderRadius: 10,
            background: 'rgba(255, 255, 255, 0.08)',
            color: '#ede9fe',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            fontSize: '0.8rem',
            fontWeight: 800,
            cursor: 'pointer',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 8,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
          }}
        >
          {desconectando ? 'Desconectando...' : 'DESCONECTAR CONTA'}
        </button>
      </div>
    );
  };

  // ── LOADING/CHECKING VERIFICATION ──
  if (checking && !installed) {
    return (
      <div className="glass-card animate-fade-in-up" style={{
        textAlign: 'center', padding: '2.5rem 1.5rem', borderRadius: 24,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: 280,
        background: 'linear-gradient(180deg, rgba(46,16,101,0.90) 0%, rgba(30,12,64,0.92) 100%)',
        border: '1px solid rgba(221, 214, 254, 0.16)',
        boxShadow: '0 26px 72px rgba(17, 7, 45, 0.56), inset 0 1px 0 rgba(255,255,255,0.14)',
        backdropFilter: 'blur(22px)',
      }}>
        <Loader2 className="animate-spin" size={42} style={{ color: '#c084fc', marginBottom: 16 }} />
        <p style={{ color: '#cbd5e1', fontSize: '0.85rem', fontWeight: 650 }}>
          Verificando proteção do dispositivo...
        </p>
      </div>
    );
  }

  // ── JA INSTALADO ──
  if (installed) {
    const progressPercent = Math.round(((15 - successCountdown) / 15) * 100);
    const isFinished = successCountdown === 0;
    return (
      <div className="glass-card animate-fade-in-up" style={{
        textAlign: 'center', padding: '2.5rem 1.5rem', borderRadius: 24,
        position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(180deg, rgba(46,16,101,0.90) 0%, rgba(30,12,64,0.92) 100%)',
        border: '1px solid rgba(221, 214, 254, 0.16)',
        boxShadow: '0 26px 72px rgba(17, 7, 45, 0.56), inset 0 1px 0 rgba(255,255,255,0.14)',
        backdropFilter: 'blur(22px)',
      }}>
        <div style={{
          position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
          width: 150, height: 150, background: isFinished ? 'rgba(16, 185, 129, 0.12)' : 'rgba(168, 85, 247, 0.1)',
          filter: 'blur(40px)', zIndex: 0,
          transition: 'background 0.5s',
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: isFinished ? 'rgba(16,185,129,.12)' : 'rgba(168,85,247,.08)',
            border: isFinished ? '2px solid #10b981' : '2px solid #c084fc',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.25rem',
            transition: 'all 0.5s',
          }}>
            {isFinished ? (
              <img
                src="/icons/logo-pwa.svg"
                alt="Aluno Conectado"
                style={{ width: '80%', height: '80%', objectFit: 'contain' }}
              />
            ) : (
              <Smartphone size={36} className="animate-pulse" style={{ color: '#c084fc' }} />
            )}
          </div>
          <h2 style={{
            fontSize: '1.3rem',
            fontWeight: 700,
            color: isFinished ? '#10b981' : '#f8fafc',
            marginBottom: '.5rem',
            transition: 'color 0.5s',
          }}>
            {isFinished ? 'Aplicativo instalado!' : 'Instalando App...'}
          </h2>
          <p style={{ fontSize: '.85rem', color: '#cbd5e1', lineHeight: 1.6 }}>
            {isFinished ? (
              <>O aplicativo foi instalado com sucesso no seu dispositivo.</>
            ) : (
              <>Aguarde um instante para que o sistema registre a instalação... Sincronizando segurança em <b>{successCountdown}s</b>...</>
            )}
          </p>

          {!isFinished ? (
            <>
              <div style={{
                width: '100%',
                height: 12,
                background: 'rgba(16, 185, 129, 0.08)',
                borderRadius: 6,
                overflow: 'hidden',
                marginTop: '1.5rem',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                position: 'relative',
                boxShadow: '0 0 12px rgba(16, 185, 129, 0.15)',
              }}>
                <div style={{
                  width: `${progressPercent}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #10b981, #34d399)',
                  boxShadow: '0 0 10px rgba(52, 211, 153, 0.6)',
                  transition: 'width 1s linear',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  {/* Internal neon shimmer effect */}
                  <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
                    animation: 'shimmer-sweep 2s infinite linear',
                    backgroundSize: '200% auto',
                  }} />
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '0.5rem',
                fontSize: '0.78rem',
                fontWeight: 750,
                color: '#34d399',
                textShadow: '0 0 8px rgba(52, 211, 153, 0.3)',
              }}>
                <span>Registrando ativação...</span>
                <span>{progressPercent}%</span>
              </div>
            </>
          ) : (
            <div style={{ marginTop: '1.75rem', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Buttons Row */}
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => setSuccessHelp('desinstalar')}
                  style={{
                    flex: 1,
                    padding: '0.85rem 1rem',
                    borderRadius: 12,
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    background: '#ffffff',
                    color: '#0f172a',
                    fontWeight: 750,
                    fontSize: '0.92rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transition: 'all 0.1s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.transform = 'none';
                  }}
                >
                  <Trash2 size={16} />
                  Desinstalar
                </button>

                <button
                  type="button"
                  onClick={() => {
                    handleOpenApp();
                    setSuccessHelp('abrir');
                  }}
                  style={{
                    flex: 1,
                    padding: '0.85rem 1rem',
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '0.92rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                    transition: 'all 0.1s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = 'brightness(1.08)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = 'none';
                    e.currentTarget.style.transform = 'none';
                  }}
                >
                  <Smartphone size={16} />
                  Abrir
                </button>
              </div>

              {/* Help Cards */}
              {successHelp === 'abrir' && (
                <div className="animate-fade-in-up" style={{
                  padding: '12px 14px',
                  borderRadius: 14,
                  background: 'rgba(16, 185, 129, 0.08)',
                  border: '1px solid rgba(52, 211, 153, 0.22)',
                  color: '#e2e8f0',
                  fontSize: '0.8rem',
                  lineHeight: 1.45,
                  textAlign: 'left',
                  boxSizing: 'border-box',
                }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                    <Info size={14} style={{ color: '#34d399' }} />
                    <strong style={{ color: '#ffffff', fontSize: '0.84rem' }}>Como abrir o aplicativo:</strong>
                  </div>
                  Se o aplicativo não abrir automaticamente, acesse a gaveta ou tela de aplicativos do seu celular e procure pelo ícone do <strong>Aluno Conectado</strong>.
                </div>
              )}

              {successHelp === 'desinstalar' && (
                <div className="animate-fade-in-up" style={{
                  padding: '12px 14px',
                  borderRadius: 14,
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  color: '#e2e8f0',
                  fontSize: '0.8rem',
                  lineHeight: 1.45,
                  textAlign: 'left',
                  boxSizing: 'border-box',
                }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                    <Info size={14} style={{ color: '#c4b5fd' }} />
                    <strong style={{ color: '#ffffff', fontSize: '0.84rem' }}>Como desinstalar o aplicativo:</strong>
                  </div>
                  Para remover o aplicativo do seu aparelho: Localize o ícone do <strong>Aluno Conectado</strong> na sua tela inicial, mantenha o dedo pressionado sobre ele e selecione a opção <strong>'Desinstalar'</strong> ou <strong>'Remover'</strong>.
                </div>
              )}

              {/* Continue to web link */}
              <div style={{ marginTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.25rem' }}>
                <button
                  type="button"
                  onClick={onSuccess}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#c4b5fd',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Continuar para o Painel no Navegador
                </button>
              </div>
            </div>
          )}

          {/* Exibir dados do usuário na tela de sucesso/carregamento */}
          {showAccountCard && renderUserInfo()}
        </div>
      </div>
    );
  }

  // ── iOS: INSTRUCOES MANUAIS ──
  if (isIOS) {
    return (
      <div className="glass-card animate-fade-in-up" style={{
        textAlign: 'center',
        padding: '16px',
        borderRadius: 24,
        position: 'relative',
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 26px 72px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255,255,255,0.10)',
        backdropFilter: 'blur(22px)',
        WebkitBackdropFilter: 'blur(22px)',
      }}>
        <style>{`
          @keyframes shimmer-sweep {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          .btn-3d-install {
            width: 100%;
            padding: 0.95rem 1.25rem;
            border-radius: 12px;
            border: none;
            background: linear-gradient(120deg, #059669 0%, #10b981 30%, #6ee7b7 50%, #10b981 70%, #059669 100%);
            background-size: 200% auto;
            color: #ffffff;
            font-size: 1.08rem;
            font-weight: 850;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            position: relative;
            box-shadow: 0 4px 0 #047857, 0 6px 15px rgba(16, 185, 129, 0.3);
            transition: transform 0.1s ease, box-shadow 0.1s ease;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
            animation: shimmer-sweep 3s infinite linear;
            margin-top: 1rem;
          }
          .btn-3d-install:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 0 #047857, 0 8px 20px rgba(16, 185, 129, 0.4);
          }
          .btn-3d-install:active {
            transform: translateY(2px);
            box-shadow: 0 2px 0 #047857, 0 4px 10px rgba(16, 185, 129, 0.2);
          }
        `}</style>
        <div style={{
          position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
          width: 150, height: 150, background: 'rgba(168, 85, 247, 0.14)',
          filter: 'blur(40px)', zIndex: 0,
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>


          <h2 style={{
            fontSize: '1.65rem',
            fontWeight: 800,
            marginBottom: '1.5rem',
            color: '#f8fafc',
            letterSpacing: '-0.02em',
          }}>
            Instalação para Apple
          </h2>

          {/* Passos em formato de lista simples */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            marginBottom: '1.75rem',
            textAlign: 'left',
            padding: '0 16px',
          }}>
            <p style={{ margin: 0, color: '#ede9fe', fontSize: '0.92rem', fontWeight: 400, lineHeight: 1.4 }}>
              1. Toque no botão Compartilhar no Safari
            </p>
            <div style={{ borderTop: '1px dashed rgba(255, 255, 255, 0.5)', margin: '6px 0', opacity: 0.5 }} />
            <p style={{ margin: 0, color: '#ede9fe', fontSize: '0.92rem', fontWeight: 400, lineHeight: 1.4 }}>
              2. Escolha "Adicionar à Tela de Início"
            </p>
            <div style={{ borderTop: '1px dashed rgba(255, 255, 255, 0.5)', margin: '6px 0', opacity: 0.5 }} />
            <p style={{ margin: 0, color: '#ede9fe', fontSize: '0.92rem', fontWeight: 400, lineHeight: 1.4 }}>
              3. Confirme tocando em "Adicionar"
            </p>
          </div>

          <button onClick={() => {
            if (!successCalledRef.current) {
              successCalledRef.current = true;
              onSuccess();
            }
          }} className="premium-btn" style={{
            width: '100%', padding: '1.1rem 1rem', borderRadius: 12, border: 'none',
            fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            marginBottom: '1rem',
          }}>
            <CheckCircle2 size={20} /> Já instalei
          </button>
          
          <button
            onClick={handleOpenApp}
            className="btn-3d-install"
          >
            <Smartphone size={20} strokeWidth={2.5} />
            Abrir no Aplicativo
          </button>

          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#c4b5fd',
                fontSize: '0.86rem',
                fontWeight: 650,
                textDecoration: 'underline',
                marginTop: '1.25rem',
                cursor: 'pointer',
                padding: '8px 16px',
                width: 'fit-content',
                margin: '12px auto 0',
                opacity: 0.85,
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.85'}
            >
              Entrar sem instalar
            </button>
          )}

          {showAccountCard && renderUserInfo()}
        </div>
      </div>
    );
  }

  // ── CHROMIUM / ANDROID / DESKTOP: Card direto sem loading ──
  const tipText = isAndroid
    ? 'Dica: O ícone estará na tela inicial ou na gaveta de aplicativos.'
    : isWindows
    ? 'Dica: O aplicativo estará na área de trabalho ou no Menu Iniciar.'
    : 'Dica: O aplicativo estará disponível no menu de aplicativos do seu sistema.';

  return (
    <div className="glass-card animate-fade-in-up" style={{
      textAlign: 'center', padding: '1.5rem 1.25rem', borderRadius: 20,
      position: 'relative', overflow: 'hidden',
    }}>
      <style>{`
        @keyframes smartphone-pulse {
          0%, 100% { border-color: rgba(168,85,247,0.3); box-shadow: 0 0 10px rgba(168,85,247,0.1); }
          10% { border-color: #a855f7; box-shadow: 0 0 20px rgba(168,85,247,0.5); }
          25% { border-color: rgba(168,85,247,0.3); box-shadow: 0 0 10px rgba(168,85,247,0.1); }
        }
        @keyframes dot-pulse-1 {
          0%, 100% { background: rgba(255,255,255,0.15); box-shadow: none; }
          15% { background: #c084fc; box-shadow: 0 0 8px #c084fc; }
          30% { background: rgba(255,255,255,0.15); box-shadow: none; }
        }
        @keyframes dot-pulse-2 {
          0%, 100% { background: rgba(255,255,255,0.15); box-shadow: none; }
          25% { background: #c084fc; box-shadow: 0 0 8px #c084fc; }
          40% { background: rgba(255,255,255,0.15); box-shadow: none; }
        }
        @keyframes dot-pulse-3 {
          0%, 100% { background: rgba(255,255,255,0.15); box-shadow: none; }
          35% { background: #34d399; box-shadow: 0 0 8px #34d399; }
          50% { background: rgba(255,255,255,0.15); box-shadow: none; }
        }
        @keyframes dot-pulse-4 {
          0%, 100% { background: rgba(255,255,255,0.15); box-shadow: none; }
          45% { background: #34d399; box-shadow: 0 0 8px #34d399; }
          60% { background: rgba(255,255,255,0.15); box-shadow: none; }
        }
        @keyframes android-pulse {
          0%, 100% { border-color: rgba(16,185,129,0.3); box-shadow: 0 0 10px rgba(16,185,129,0.1); }
          55% { border-color: #34d399; box-shadow: 0 0 20px rgba(16,185,129,0.5); }
          70% { border-color: rgba(16,185,129,0.3); box-shadow: 0 0 10px rgba(16,185,129,0.1); }
        }
        @keyframes shimmer-sweep {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .btn-3d-install {
          width: 100%;
          padding: 0.95rem 1.25rem;
          border-radius: 12px;
          border: none;
          background: linear-gradient(120deg, #059669 0%, #10b981 30%, #6ee7b7 50%, #10b981 70%, #059669 100%);
          background-size: 200% auto;
          color: #ffffff;
          font-size: 1.08rem;
          font-weight: 850;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          position: relative;
          box-shadow: 0 4px 0 #047857, 0 6px 15px rgba(16, 185, 129, 0.3);
          transition: transform 0.1s ease, box-shadow 0.1s ease;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
          animation: shimmer-sweep 3s infinite linear;
          margin-top: 1rem;
        }
        .btn-3d-install:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 0 #047857, 0 8px 20px rgba(16, 185, 129, 0.4);
        }
        .btn-3d-install:active {
          transform: translateY(2px);
          box-shadow: 0 2px 0 #047857, 0 4px 10px rgba(16, 185, 129, 0.2);
        }
        .btn-3d-install:disabled {
          background: rgba(255,255,255,0.05);
          color: #94a3b8;
          box-shadow: none;
          cursor: not-allowed;
          transform: none;
          animation: none;
        }
      `}</style>
      <div style={{
        position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
        width: 150, height: 150, background: 'rgba(168, 85, 247, 0.1)',
        filter: 'blur(40px)', zIndex: 0,
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <h2 style={{
          fontSize: '1.65rem', fontWeight: 800, marginBottom: '1.25rem',
          color: '#f8fafc', letterSpacing: '-0.02em',
        }}>
          Instalação do App
        </h2>

        {/* Animated Smartphone-to-Android pairing/transmission graphic */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 12, margin: '0 auto 1.5rem', width: 'fit-content'
        }}>
          {/* Aluno Conectado Logo Circle */}
          <div style={{
            width: 76, height: 76, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(168,85,247,0.2) 0%, rgba(168,85,247,0.05) 100%)',
            border: '2px solid rgba(168,85,247,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'smartphone-pulse 3s infinite ease-in-out',
            flexShrink: 0,
            overflow: 'hidden',
          }}>
            <img
              src="/icons/logo-pwa.svg"
              alt="Aluno Conectado"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>

          {/* Connection dots (4 dots) */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', animation: 'dot-pulse-1 3s infinite ease-in-out' }} />
            <span style={{ width: 6, height: 6, borderRadius: '50%', animation: 'dot-pulse-2 3s infinite ease-in-out' }} />
            <span style={{ width: 6, height: 6, borderRadius: '50%', animation: 'dot-pulse-3 3s infinite ease-in-out' }} />
            <span style={{ width: 6, height: 6, borderRadius: '50%', animation: 'dot-pulse-4 3s infinite ease-in-out' }} />
          </div>

          {/* Smartphone (Client) Circle */}
          <div style={{
            width: 76, height: 76, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0.03) 100%)',
            border: '2px solid rgba(16,185,129,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'android-pulse 3s infinite ease-in-out',
            flexShrink: 0,
          }}>
            <Smartphone size={32} strokeWidth={1.5} style={{ color: '#34d399' }} />
          </div>
        </div>

        <p style={{ fontSize: '0.90rem', color: '#cbd5e1', fontWeight: 500, marginBottom: '1.5rem', lineHeight: 1.6, padding: '0 12px' }}>
          {isDetected 
            ? 'O aplicativo já está instalado no seu dispositivo.' 
            : 'Instale o aplicativo oficial para ativar recursos avançados com total segurança e proteção biométrica integrada.'}
        </p>

        {!isDetected && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <button
              onClick={handleInstall}
              disabled={installing}
              className="btn-3d-install"
            >
              <Download size={20} strokeWidth={2.5} />
              {installing ? 'Instalando...' : 'Instalar Aplicativo'}
            </button>
            {onSkip && (
              <button
                type="button"
                onClick={onSkip}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#c4b5fd',
                  fontSize: '0.86rem',
                  fontWeight: 650,
                  textDecoration: 'underline',
                  marginTop: '1.25rem',
                  cursor: 'pointer',
                  padding: '8px 16px',
                  width: 'fit-content',
                  margin: '12px auto 0',
                  opacity: 0.85,
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.85'}
              >
                Entrar sem instalar
              </button>
            )}
            {showAccountCard && renderUserInfo()}
          </div>
        )}

        {/* Botão de abrir o aplicativo se já estiver instalado */}
        {isDetected && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <button
              onClick={handleOpenApp}
              className="btn-3d-install"
            >
              <Smartphone size={20} strokeWidth={2.5} />
              Abrir no Aplicativo
            </button>
            {onSkip && (
              <button
                type="button"
                onClick={onSkip}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#c4b5fd',
                  fontSize: '0.86rem',
                  fontWeight: 650,
                  textDecoration: 'underline',
                  marginTop: '1.25rem',
                  cursor: 'pointer',
                  padding: '8px 16px',
                  width: 'fit-content',
                  margin: '12px auto 0',
                  opacity: 0.85,
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.85'}
              >
                Entrar sem instalar
              </button>
            )}
            {showAccountCard && renderUserInfo()}
          </div>
        )}

        {/* Erro */}
        {error && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            <div style={{
              padding: '0.75rem 1rem', borderRadius: 10, marginTop: '1rem',
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
              display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left',
            }}>
              <AlertTriangle size={16} style={{ color: '#fca5a5', flexShrink: 0, marginTop: 2 }} />
              <p style={{ color: '#fca5a5', fontSize: '0.8rem', margin: 0, lineHeight: 1.4 }}>
                {error}
              </p>
            </div>
            <button
              type="button"
              onClick={onSkip}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#a78bfa',
                fontSize: '0.82rem',
                fontWeight: 700,
                cursor: 'pointer',
                textDecoration: 'underline',
                marginTop: '0.25rem',
                padding: '6px 0',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                textShadow: '0 0 10px rgba(167,139,250,0.3)'
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#c084fc'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#a78bfa'; }}
            >
              Continuar no navegador
            </button>
          </div>
        )}



        {/* Ocultado a pedido do usuário */}
      </div>
    </div>
  );
}
